import * as React from "react";
import { cn } from "../../lib/utils";
import { Button } from "./Button";
import { TD, TR } from "./Table";

/**
 * The cell vocabulary. Every mark a row can carry, and the one rule that says
 * which mark a given fact gets.
 *
 * 🔴 THE CENTRAL RULE, ENCODED ONCE:
 *
 *     A PLAIN MISSING VALUE RENDERS `—` WITH NO ACTION.
 *     A WORKFLOW STATE SOMEONE COULD ACT ON CARRIES ITS OWN DOOR.
 *
 * Both halves matter. A blank phone number is not a task — dressing it as
 * "+ Add phone" turns a table of facts into a to-do list nobody wrote, and a
 * reader scanning for what needs doing has to read every dash to find out none
 * of them do. Conversely, a signature that has not been collected IS a task,
 * and rendering it as `—` hides work behind punctuation.
 *
 * The distinction is not "is it empty" — it is "is there a workflow behind it".
 * That is a fact about the FIELD, so it is declared by the caller once per
 * column, not inferred per row.
 *
 * BADGE VOCABULARY — semantic, not decorative. Four marks, four meanings; a
 * fifth colour would be a fifth meaning nobody has defined:
 *
 *   CATEGORY  what kind of thing this is         gold/black pill
 *   STATE     present                            green pill
 *   STATE     absent                             muted grey prose, no pill
 *   EXCEPTION something is wrong with this row    outline pill, inline after
 *             (attention → amber, overdue → red)  the row's own name
 *
 * State-absent is deliberately NOT a grey pill. A pill is a claim that a fact
 * was recorded; "we have nothing" is the absence of a fact, and giving it the
 * same shape as a recorded one makes an empty column look populated at a
 * glance. Exceptions sit inline after the NAME rather than in a status column
 * because that is where the eye already is when it is scanning for which row
 * to work.
 */

/* ------------------------------------------------------------------ marks */

export interface CategoryBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  children: React.ReactNode;
}

/** WHAT KIND of thing this row is. Never a status — categories do not change. */
export function CategoryBadge({ className, children, ...props }: CategoryBadgeProps) {
  return (
    <span
      className={cn(
        "inline-flex items-center whitespace-nowrap rounded-pill bg-onyx px-space-2 py-[2px]",
        "font-display text-[10px] font-bold uppercase tracking-label text-gold",
        className,
      )}
      data-phx-mark="category"
      {...props}
    >
      {children}
    </span>
  );
}

export interface StateMarkProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** Has the thing happened / been recorded? */
  present: boolean;
  /** Label for the recorded state, e.g. "Signed". */
  presentLabel: React.ReactNode;
  /** Prose for the not-recorded state. Defaults to "Not recorded". */
  absentLabel?: React.ReactNode;
}

/** Present → green pill. Absent → muted prose, because absence is not a fact. */
export function StateMark({
  present,
  presentLabel,
  absentLabel = "Not recorded",
  className,
  ...props
}: StateMarkProps) {
  if (!present) {
    return (
      <span
        className={cn("text-caption text-gray-mid", className)}
        data-phx-mark="state-absent"
        {...props}
      >
        {absentLabel}
      </span>
    );
  }
  return (
    <span
      className={cn(
        "inline-flex items-center whitespace-nowrap rounded-pill border border-status-success",
        "bg-status-success/15 px-space-2 py-[2px] text-caption font-medium leading-none text-status-success",
        className,
      )}
      data-phx-mark="state-present"
      {...props}
    >
      {presentLabel}
    </span>
  );
}

export type ExceptionSeverity = "attention" | "overdue";

export interface ExceptionBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  severity?: ExceptionSeverity;
  children: React.ReactNode;
}

/**
 * Something is wrong with THIS row. Outline rather than filled: an exception is
 * an annotation on a row that still exists, not a status the row now has.
 *
 * Amber uses `--status-warning-ink` for text and border, matching `StatusBadge`
 * — the fill-weight amber is 2.55:1 as small text on its own tint, under AA.
 */
export function ExceptionBadge({
  severity = "attention",
  className,
  children,
  ...props
}: ExceptionBadgeProps) {
  return (
    <span
      className={cn(
        "ml-space-2 inline-flex items-center whitespace-nowrap rounded-pill border bg-transparent",
        "px-space-2 py-[1px] text-caption font-medium leading-none align-middle",
        severity === "overdue"
          ? "border-status-error text-status-error"
          : "border-status-warning-ink text-status-warning-ink",
        className,
      )}
      data-phx-mark="exception"
      data-severity={severity}
      {...props}
    >
      {children}
    </span>
  );
}

/**
 * Nothing was recorded, and nothing is owed. An em dash, and an accessible name
 * so a screen reader does not read a bare punctuation mark or skip the cell.
 */
export function MissingValue({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) {
  return (
    <span className={cn("text-gray-mid", className)} data-phx-mark="missing" {...props}>
      <span aria-hidden>—</span>
      <span className="sr-only">Not recorded</span>
    </span>
  );
}

/* ------------------------------------------------------------------ doors */

/**
 * A door is the way a reader acts on a missing workflow state from where they
 * found it. Three kinds, and the kind must be DECLARED — the wrong one is an
 * interface that lies.
 *
 *   "stamp"     A control that records the fact in place. The reader presses it
 *               and the row changes. Safe to render as a button because the
 *               button does exactly what it says.
 *
 *   "derived"   🔴 THE STATE IS NOT WRITABLE HERE. It is computed from
 *               something else — an approval elsewhere, a payment clearing, a
 *               worker. So the door IS THAT STATUS CHANGE, and the row SAYS SO:
 *               with `onClick`, the control performs the real change and is
 *               labelled with it ("Approve the report"), never with the derived
 *               state ("Mark signed off"). Without `onClick` it is prose naming
 *               what will set it. NEVER RENDER A CONTROL THAT CANNOT DO WHAT IT
 *               APPEARS TO DO.
 *
 *   "document"  The fact arrives as a file. The control opens an upload, and
 *               the copy says where the file ends up, because a reader who
 *               uploads here will look for it in the Documents tab.
 */
export type CellDoor =
  | {
      kind: "stamp";
      label: string;
      onClick: () => void;
      disabled?: boolean;
      /** Why it is unavailable. Rendered as prose INSTEAD of a dead button. */
      disabledReason?: string;
    }
  | {
      kind: "derived";
      /** What actually sets this state — the words the reader needs. */
      explain: string;
      /** The real status change, if it can be made from here. */
      label?: string;
      onClick?: () => void;
    }
  | {
      kind: "document";
      label: string;
      onClick: () => void;
      disabled?: boolean;
      disabledReason?: string;
      /** Where the file lands. Defaults to the Documents tab. */
      destination?: string;
    };

export interface CellDoorProps {
  door: CellDoor;
  className?: string;
}

/** Renders a door. Exported so a hand-rolled table can use the same vocabulary. */
export function CellDoorControl({ door, className }: CellDoorProps) {
  if (door.kind === "derived") {
    // No control offered → prose only. This is the branch that keeps the
    // promise: a derived state with nothing to press must not grow a button.
    if (!door.onClick || !door.label) {
      return (
        <span
          className={cn("text-caption italic text-gray-mid", className)}
          data-phx-door="derived"
          data-phx-door-actionable="false"
        >
          {door.explain}
        </span>
      );
    }
    return (
      <span className={cn("inline-flex flex-col items-start gap-space-1", className)}>
        <Button
          variant="text"
          size="sm"
          onClick={door.onClick}
          className="px-0"
          data-phx-door="derived"
          data-phx-door-actionable="true"
        >
          {door.label}
        </Button>
        <span className="text-caption italic text-gray-mid">{door.explain}</span>
      </span>
    );
  }

  if (door.disabled) {
    return (
      <span
        className={cn("text-caption italic text-gray-mid", className)}
        data-phx-door={door.kind}
        data-phx-door-actionable="false"
      >
        {door.disabledReason ?? "Not available yet"}
      </span>
    );
  }

  const destination =
    door.kind === "document" ? (door.destination ?? "the Documents tab") : null;

  return (
    <span className={cn("inline-flex flex-col items-start gap-space-1", className)}>
      <Button
        variant="text"
        size="sm"
        onClick={door.onClick}
        className="px-0"
        data-phx-door={door.kind}
        data-phx-door-actionable="true"
      >
        {door.label}
      </Button>
      {destination && (
        <span className="text-caption text-gray-mid">Filed to {destination}.</span>
      )}
    </span>
  );
}

/* -------------------------------------------------------------- the rule */

export interface TableValueProps {
  /** The recorded value. `null` / `undefined` / `""` all count as missing. */
  value: React.ReactNode;
  /**
   * The door for this FIELD, when the field is a workflow state. Omit it and a
   * missing value is `—` with no action — which is the right answer for every
   * plain fact.
   */
  door?: CellDoor;
  className?: string;
}

function isMissing(value: React.ReactNode): boolean {
  return value == null || value === false || value === "";
}

/**
 * The rule, in one place. Give it the value and — only for a workflow state —
 * the door. It decides nothing else.
 */
export function TableValue({ value, door, className }: TableValueProps) {
  if (!isMissing(value)) return <>{value}</>;
  if (!door) return <MissingValue className={className} />;
  return <CellDoorControl door={door} className={className} />;
}

/* --------------------------------------------------------- empty section */

export interface EmptyStateRowProps {
  /** Column count to span. `SubTable` supplies this itself. */
  colSpan: number;
  /** What is not here, in the reader's words. */
  message: string;
  /**
   * How to make it exist. 🔴 REQUIRED to declare its kind — an empty section
   * whose door type is left implicit is exactly how a "Mark complete" button
   * appears over a state the button cannot set.
   */
  door?: CellDoor;
  className?: string;
  "data-testid"?: string;
}

/** The empty body of a table: one full-width row, the message, and the door. */
export function EmptyStateRow({
  colSpan,
  message,
  door,
  className,
  "data-testid": testId,
}: EmptyStateRowProps) {
  return (
    <TR className={cn("hover:bg-transparent", className)} data-testid={testId}>
      <TD colSpan={colSpan} className="py-space-5 text-center">
        <div className="flex flex-col items-center gap-space-2">
          <span className="text-small text-gray-mid">{message}</span>
          {door && <CellDoorControl door={door} />}
        </div>
      </TD>
    </TR>
  );
}
